// Sound Manager - Unified catalog of all WAV sounds
// Used by audioService for playback and EventRulesPage for the sound picker

export interface SoundDefinition {
  id: string;
  label: string;
  category: 'alarm' | 'notification' | 'access' | 'system';
  description: string;
  file: string; // WAV file path (relative to public/)
}

// ============================================
// ALARM SOUNDS (16)
// ============================================

const ALARM_SOUNDS: SoundDefinition[] = [
  { id: 'alarm_critical', label: 'Critical Alarm', category: 'alarm', description: 'Urgent siren', file: '/sounds/alarm_critical.wav' },
  { id: 'alarm_high', label: 'High Priority', category: 'alarm', description: 'Alert tone', file: '/sounds/alarm_high.wav' },
  { id: 'alarm_medium', label: 'Medium Priority', category: 'alarm', description: 'Warning tone', file: '/sounds/alarm_medium.wav' },
  { id: 'alarm_low', label: 'Low Priority', category: 'alarm', description: 'Soft alert', file: '/sounds/alarm_low.wav' },
  { id: 'intrusion', label: 'Intrusion Alert', category: 'alarm', description: 'Intrusion alarm', file: '/sounds/intrusion.wav' },
  { id: 'panic', label: 'Panic Alarm', category: 'alarm', description: 'Emergency panic', file: '/sounds/panic.wav' },
  { id: 'fire', label: 'Fire Alarm', category: 'alarm', description: 'Fire alarm', file: '/sounds/fire.wav' },
  { id: 'glass_break', label: 'Glass Break', category: 'alarm', description: 'Glass break detection', file: '/sounds/glass_break.wav' },
  { id: 'door_forced', label: 'Door Forced', category: 'alarm', description: 'Security breach', file: '/sounds/door_forced.wav' },
  { id: 'duress', label: 'Duress Alarm', category: 'alarm', description: 'Duress/coercion', file: '/sounds/duress.wav' },
  { id: 'tamper', label: 'Tamper Alert', category: 'alarm', description: 'Device tamper', file: '/sounds/tamper.wav' },
  { id: 'communication_failure', label: 'Comm Failure', category: 'alarm', description: 'Communication lost', file: '/sounds/communication_failure.wav' },
  { id: 'ac_loss', label: 'AC Power Loss', category: 'alarm', description: 'Power failure', file: '/sounds/ac_loss.wav' },
  { id: 'battery_low', label: 'Battery Low', category: 'alarm', description: 'Low battery', file: '/sounds/battery_low.wav' },
  { id: 'video_loss', label: 'Video Loss', category: 'alarm', description: 'Camera signal lost', file: '/sounds/video_loss.wav' },
  { id: 'sla_breach', label: 'SLA Breach', category: 'alarm', description: 'SLA deadline breached', file: '/sounds/sla_breach.wav' },
];

// ============================================
// NOTIFICATION SOUNDS (18)
// ============================================

const NOTIFICATION_SOUNDS: SoundDefinition[] = [
  { id: 'chime', label: 'Chime', category: 'notification', description: 'Pleasant chime', file: '/sounds/chime.wav' },
  { id: 'beep', label: 'Beep', category: 'notification', description: 'Simple beep', file: '/sounds/beep.wav' },
  { id: 'ding', label: 'Ding', category: 'notification', description: 'Notification ding', file: '/sounds/ding.wav' },
  { id: 'pop', label: 'Pop', category: 'notification', description: 'Bubble pop', file: '/sounds/pop.wav' },
  { id: 'bell', label: 'Bell', category: 'notification', description: 'Bell ring', file: '/sounds/bell.wav' },
  { id: 'click', label: 'Click', category: 'notification', description: 'UI click', file: '/sounds/click.wav' },
  { id: 'success', label: 'Success', category: 'notification', description: 'Success tone', file: '/sounds/success.wav' },
  { id: 'error', label: 'Error', category: 'notification', description: 'Error tone', file: '/sounds/error.wav' },
  { id: 'warning', label: 'Warning', category: 'notification', description: 'Warning tone', file: '/sounds/warning.wav' },
  { id: 'message', label: 'Message', category: 'notification', description: 'New message', file: '/sounds/message.wav' },
  { id: 'door_open', label: 'Door Open', category: 'notification', description: 'Door opening', file: '/sounds/door_open.wav' },
  { id: 'access_granted', label: 'Access Granted', category: 'notification', description: 'Access confirmed', file: '/sounds/access_granted.wav' },
  { id: 'access_denied', label: 'Access Denied', category: 'notification', description: 'Access denied buzzer', file: '/sounds/access_denied.wav' },
  { id: 'motion', label: 'Motion', category: 'notification', description: 'Motion detected', file: '/sounds/motion.wav' },
  { id: 'escalation', label: 'Escalation', category: 'notification', description: 'Event escalated', file: '/sounds/escalation.wav' },
  { id: 'sop_step', label: 'SOP Step', category: 'notification', description: 'SOP step complete', file: '/sounds/sop_step.wav' },
  { id: 'incident_created', label: 'Incident Created', category: 'notification', description: 'New incident', file: '/sounds/incident_created.wav' },
  { id: 'timer', label: 'Timer', category: 'notification', description: 'Timer alert', file: '/sounds/timer.wav' },
  { id: 'camera_shutter', label: 'Camera Shutter', category: 'notification', description: 'Camera snapshot shutter', file: '/sounds/camera_shutter.wav' },
];

// ============================================
// UNIFIED CATALOG
// ============================================

export const ALL_SOUNDS: SoundDefinition[] = [...ALARM_SOUNDS, ...NOTIFICATION_SOUNDS];

export const SOUNDS_BY_ID = new Map<string, SoundDefinition>(
  ALL_SOUNDS.map((s) => [s.id, s])
);

export const SOUNDS_BY_CATEGORY = {
  alarm: ALARM_SOUNDS,
  notification: NOTIFICATION_SOUNDS,
  access: ALL_SOUNDS.filter((s) => s.id.startsWith('access_') || s.id === 'door_open'),
  system: ALL_SOUNDS.filter((s) => ['communication_failure', 'ac_loss', 'battery_low', 'video_loss'].includes(s.id)),
};

/**
 * Get sound definition by ID
 */
export function getSound(id: string): SoundDefinition | undefined {
  return SOUNDS_BY_ID.get(id);
}

/**
 * Get all sounds grouped by category
 */
export function getSoundsByCategory(): Array<{ category: string; label: string; sounds: SoundDefinition[] }> {
  return [
    { category: 'alarm', label: 'Alarm Sounds', sounds: ALARM_SOUNDS },
    { category: 'notification', label: 'Notification Sounds', sounds: NOTIFICATION_SOUNDS },
  ];
}

// ============================================
// TEXT-TO-SPEECH (Browser SpeechSynthesis API)
// Self-contained, works offline for English + Hebrew
// ============================================

// Voice persona definitions (must match VoiceSettingsPage.tsx)
const PERSONA_VOICE_HINTS: Record<string, { voiceNameHint: string[]; rate: number; pitch: number; lang: string }> = {
  'david-en': { voiceNameHint: ['Microsoft David', 'Google US English Male', 'Alex', 'Daniel', 'Fred'], rate: 1.0, pitch: 0.95, lang: 'en-US' },
  'james-en': { voiceNameHint: ['Microsoft Mark', 'Google US English Male', 'Alex', 'Tom'], rate: 0.85, pitch: 0.9, lang: 'en-US' },
  'captain-en': { voiceNameHint: ['Microsoft David', 'Google US English Male', 'Daniel'], rate: 1.25, pitch: 1.05, lang: 'en-US' },
  'sarah-en': { voiceNameHint: ['Microsoft Zira', 'Google US English Female', 'Samantha', 'Victoria', 'Karen'], rate: 1.0, pitch: 1.1, lang: 'en-US' },
  'emma-en': { voiceNameHint: ['Samantha', 'Microsoft Zira', 'Google US English Female', 'Fiona'], rate: 0.88, pitch: 1.15, lang: 'en-US' },
  'alert-female-en': { voiceNameHint: ['Microsoft Zira', 'Google US English Female', 'Samantha', 'Kate'], rate: 1.2, pitch: 1.2, lang: 'en-US' },
  'asaf-he': { voiceNameHint: ['Microsoft Asaf', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew', 'Asaf'], rate: 1.0, pitch: 0.95, lang: 'he-IL' },
  'calm-male-he': { voiceNameHint: ['Microsoft Asaf', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew', 'Asaf'], rate: 0.85, pitch: 0.9, lang: 'he-IL' },
  'urgent-male-he': { voiceNameHint: ['Microsoft Asaf', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew', 'Asaf'], rate: 1.25, pitch: 1.05, lang: 'he-IL' },
  'carmit-he': { voiceNameHint: ['Carmit', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew'], rate: 1.0, pitch: 1.1, lang: 'he-IL' },
  'calm-female-he': { voiceNameHint: ['Carmit', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew'], rate: 0.88, pitch: 1.15, lang: 'he-IL' },
  'urgent-female-he': { voiceNameHint: ['Carmit', 'Google Hebrew', 'Google עברית', 'he-IL', 'Hebrew'], rate: 1.2, pitch: 1.2, lang: 'he-IL' },
};

function getVoiceSettings() {
  const get = (key: string, fallback: string) => localStorage.getItem('commander-voice-' + key) || fallback;
  return {
    enabled: get('tts-enabled', 'true') === 'true',
    lang: get('tts-lang', 'en-US'),
    volume: parseInt(get('tts-volume', '80')),
    persona: get('tts-persona', 'david-en'),
    rate: parseFloat(get('tts-rate', '1.0')),
    pitch: parseFloat(get('tts-pitch', '1.0')),
  };
}

/**
 * Speak text aloud using the browser's built-in SpeechSynthesis API.
 * Uses voice settings from System Settings > Voice & Audio (persona, rate, pitch, language).
 * Falls back to provided lang/volume if no settings configured.
 */
export function speakText(text: string, lang?: string, volume?: number): void {
  if (!('speechSynthesis' in window)) {
    console.warn('SpeechSynthesis API not available in this browser');
    return;
  }

  const settings = getVoiceSettings();

  // Respect global TTS enabled toggle
  if (!settings.enabled) return;

  // Use system settings as defaults, allow per-call overrides
  const effectiveLang = lang || settings.lang;
  const effectiveVolume = volume ?? settings.volume;

  // Cancel any ongoing speech
  window.speechSynthesis.cancel();

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = effectiveLang;
  utterance.volume = Math.max(0, Math.min(1, effectiveVolume / 100));
  utterance.rate = settings.rate;
  utterance.pitch = settings.pitch;

  // Try to pick a voice matching the selected persona
  const voices = window.speechSynthesis.getVoices();
  const persona = PERSONA_VOICE_HINTS[settings.persona];
  let matchedVoice: SpeechSynthesisVoice | undefined;

  if (persona) {
    const langPrefix = effectiveLang.split('-')[0];
    const langVoices = voices.filter(v => v.lang.startsWith(langPrefix));
    const femalePattern = /female|zira|samantha|victoria|karen|fiona|carmit|woman|girl/i;
    const malePattern = /male|david|mark|daniel|alex|fred|tom|asaf|man|guy/i;
    const isFemale = persona.voiceNameHint.some(h => femalePattern.test(h));

    // 1. Match by persona voice hints (case-insensitive)
    for (const hint of persona.voiceNameHint) {
      const hintLower = hint.toLowerCase();
      matchedVoice = langVoices.find(v => v.name.toLowerCase().includes(hintLower));
      if (matchedVoice) break;
    }

    // 2. Gender-based fallback
    if (!matchedVoice) {
      if (isFemale) {
        matchedVoice = langVoices.find(v => femalePattern.test(v.name) && !malePattern.test(v.name));
      } else {
        matchedVoice = langVoices.find(v => malePattern.test(v.name) && !femalePattern.test(v.name))
          || langVoices.find(v => !femalePattern.test(v.name));
      }
    }

    // 3. Any voice for language
    if (!matchedVoice) matchedVoice = langVoices[0];
  }

  // Fallback: any voice matching the language
  if (!matchedVoice) {
    const langPrefix = effectiveLang.split('-')[0];
    matchedVoice = voices.find((v) => v.lang === effectiveLang) || voices.find((v) => v.lang.startsWith(langPrefix));
  }

  if (matchedVoice) {
    utterance.voice = matchedVoice;
  }

  window.speechSynthesis.speak(utterance);
}

/**
 * Play a custom uploaded sound by URL.
 */
export function playCustomSound(url: string, volume: number = 80): void {
  const audio = new Audio(url);
  audio.volume = Math.max(0, Math.min(1, volume / 100));
  audio.play().catch((err) => console.warn('Custom sound play failed:', err));
}
